import React, { useState } from 'react';
import { MenuIcon, XIcon } from 'lucide-react';
type PageType = 'home' | 'commercial' | 'services' | 'residential' | 'reservation' | 'cinema';
interface HeaderProps {
  currentPage: PageType;
  onNavigate: (page: PageType) => void;
  activeSection?: string | null;
}
const navItems: {
  label: string;
  page: PageType;
  sectionId?: string;
}[] = [{
  label: 'HOME',
  page: 'home'
}, {
  label: 'SLUŽBY',
  page: 'services'
}, {
  label: 'KOMERČNÍ ŘEŠENÍ',
  page: 'commercial'
}, {
  label: 'REZIDENTNÍ ŘEŠENÍ',
  page: 'residential'
}, {
  label: 'O NÁS',
  page: 'home',
  sectionId: 'onas'
}, {
  label: 'KONTAKT',
  page: 'home',
  sectionId: 'kontakt'
}];
interface BreadcrumbItem {
  label: string;
  page?: PageType;
}
const breadcrumbs: Record<PageType, BreadcrumbItem[] | null> = {
  home: null,
  commercial: [{
    label: 'KOMERČNÍ ŘEŠENÍ'
  }],
  services: [{
    label: 'SLUŽBY'
  }],
  residential: [{
    label: 'REZIDENTNÍ ŘEŠENÍ'
  }],
  reservation: [{
    label: 'KOMERČNÍ ŘEŠENÍ',
    page: 'commercial'
  }, {
    label: 'REZERVAČNÍ SYSTÉM'
  }],
  cinema: [{
    label: 'REZIDENTNÍ ŘEŠENÍ',
    page: 'residential'
  }, {
    label: 'PRIVÁTNÍ KINOSÁL'
  }]
};
const BreadcrumbArrow = () => <svg width="10" height="10" viewBox="0 0 20 20" fill="none" className="opacity-50 flex-shrink-0" data-id="element-81">
    <path d="M2 2 L14 10 L2 18" stroke="#999" strokeWidth="2.5" strokeLinejoin="miter" fill="none" data-id="element-82" />
  </svg>;
export function Header({
  currentPage,
  onNavigate,
  activeSection
}: HeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isActive = (item: (typeof navItems)[0]) => {
    if (currentPage === 'home') {
      if (item.sectionId && activeSection === item.sectionId) return true;
      if (item.label === 'HOME' && (!activeSection || activeSection === 'hero')) return true;
      return false;
    }
    if (currentPage === 'reservation' && item.page === 'commercial' && !item.sectionId) return true;
    if (currentPage === 'cinema' && item.page === 'residential' && !item.sectionId) return true;
    if (item.page === currentPage && !item.sectionId) return true;
    return false;
  };
  const handleNavClick = (item: (typeof navItems)[0]) => {
    setMobileMenuOpen(false);
    if (item.sectionId && currentPage === 'home') {
      const el = document.getElementById(item.sectionId);
      if (el) el.scrollIntoView({
        behavior: 'smooth'
      });
    } else {
      onNavigate(item.page);
    }
  };
  const currentBreadcrumb = breadcrumbs[currentPage];
  return <header className="w-full sticky top-0 z-50" data-id="element-83">
      {/* Logo area */}
      <div className="bg-gradient-to-b from-white to-[#E9E9E9]" data-id="element-84">
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between" data-id="element-85">
          <button onClick={() => onNavigate('home')} className="block hover:opacity-80 transition-opacity" data-id="element-86">
            <img src="https://cdn.magicpatterns.com/uploads/k5fHHKued9vuhivXuUB9Kx/LOGO_LANGARD.svg" alt="LANGARD" className="h-10 w-auto" data-id="element-87" />
          </button>
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 text-[#707070] hover:text-[#404040] transition-colors" aria-label="Menu" data-id="element-88">
            {mobileMenuOpen ? <XIcon className="w-6 h-6" data-id="element-89" /> : <MenuIcon className="w-6 h-6" data-id="element-90" />}
          </button>
        </div>
      </div>

      {/* Double line separator */}
      <div className="border-t border-[#ccc]" data-id="element-91" />
      <div className="h-[3px]" data-id="element-92" />
      <div className="border-t border-[#ccc]" data-id="element-93" />

      {/* Desktop Navigation */}
      <nav className="bg-white hidden md:block" data-id="element-94">
        <div className="max-w-6xl mx-auto px-6" data-id="element-95">
          <ul className="flex items-center justify-end gap-10" data-id="element-96">
            {navItems.map(item => <li key={item.label} data-id="element-97">
                <button onClick={() => handleNavClick(item)} className={`text-sm tracking-wide transition-colors relative py-[8px] ${isActive(item) ? 'text-[#707070] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[3px] after:bg-[#707070]' : 'text-[#707070] hover:after:absolute hover:after:bottom-0 hover:after:left-0 hover:after:right-0 hover:after:h-[3px] hover:after:bg-[#c0c0c0]'}`} data-id="element-98">
                  {item.label}
                </button>
              </li>)}
          </ul>
        </div>
        <div className="border-b border-[#d5d5d5]" data-id="element-99" />
      </nav>

      {/* Mobile Navigation */}
      {mobileMenuOpen && <nav className="bg-white md:hidden border-b border-[#d5d5d5]" data-id="element-100">
          <div className="max-w-6xl mx-auto px-6 py-2" data-id="element-101">
            <ul className="flex flex-col" data-id="element-102">
              {navItems.map(item => <li key={item.label} data-id="element-103">
                  <button onClick={() => handleNavClick(item)} className={`w-full text-left text-sm tracking-wide transition-colors py-3 border-b border-[#eee] last:border-b-0 ${isActive(item) ? 'text-[#505050] font-medium' : 'text-[#707070]'}`} data-id="element-104">
                    {item.label}
                  </button>
                </li>)}
            </ul>
          </div>
        </nav>}

      {/* Breadcrumb */}
      {currentBreadcrumb && <div className="bg-white" data-id="element-105">
          <div className="max-w-6xl mx-auto px-6 py-3" data-id="element-106">
            <div className="flex items-center gap-2 text-sm text-[#999] uppercase tracking-wide" data-id="element-107">
              <button onClick={() => onNavigate('home')} className="hover:text-[#707070] transition-colors" data-id="element-108">
                HOME
              </button>
              <BreadcrumbArrow data-id="element-109" />
              {currentBreadcrumb.map((crumb, i) => <span key={i} className="flex items-center gap-2" data-id="element-110">
                  {i > 0 && <BreadcrumbArrow data-id="element-111" />}
                  {crumb.page ? <button onClick={() => onNavigate(crumb.page!)} className="hover:text-[#707070] transition-colors" data-id="element-112">
                      {crumb.label}
                    </button> : <span data-id="element-113">{crumb.label}</span>}
                </span>)}
            </div>
          </div>
        </div>}
    </header>;
}